/**
 * Web Audio SFX engine for the web app — synthesised tones, zero files, offline.
 * Mirrors the mobile app's src/lib/sfx.web.ts catalog 1:1 so both platforms
 * sound identical. Prefs sync app↔web via /me/preferences (sfx_enabled /
 * sfx_volume / sfx_tap); we cache them in localStorage so playback is instant
 * and works before the first fetch. Browsers block audio before a user gesture,
 * so we arm the AudioContext on the first pointerdown/keydown.
 */
const N = { C4: 262, C5: 523, E5: 659, G5: 784, A5: 880, C6: 1047 }

// Catalog — keep in sync with the mobile app (src/lib/sfx.web.ts + gen-sfx.cjs).
const SOUNDS = {
  success: [
    { freq: N.C5, dur: 0.16, gain: 0.22 },
    { freq: N.E5, at: 0.1, dur: 0.16, gain: 0.22 },
    { freq: N.G5, at: 0.2, dur: 0.3, gain: 0.24 },
  ],
  error: [
    { freq: N.E5, dur: 0.14, gain: 0.2, type: 'triangle' },
    { freq: N.C4, at: 0.12, dur: 0.26, gain: 0.2, type: 'triangle' },
  ],
  login: [
    { freq: N.G5, dur: 0.16, gain: 0.2 },
    { freq: N.C6, at: 0.12, dur: 0.3, gain: 0.22 },
  ],
  notify: [
    { freq: N.E5, dur: 0.12, gain: 0.18 },
    { freq: N.A5, at: 0.1, dur: 0.24, gain: 0.2 },
  ],
  break: [
    { freq: N.G5, dur: 0.12, gain: 0.16 },
    { freq: N.E5, at: 0.1, dur: 0.2, gain: 0.16 },
  ],
  submit: [
    { freq: N.C5, dur: 0.1, gain: 0.16 },
    { freq: N.G5, at: 0.08, dur: 0.2, gain: 0.2 },
  ],
  tap: [{ freq: N.A5, dur: 0.05, gain: 0.1 }],
}

let ctx = null
let armed = false

function pref(key, fallback) {
  try { const v = localStorage.getItem(key); return v == null ? fallback : v } catch { return fallback }
}
function prefEnabled() { return pref('sfx.enabled', '1') !== '0' }
function prefTap() { return pref('sfx.tap', '0') === '1' }
function prefVolume() {
  const n = parseFloat(pref('sfx.volume', '0.7'))
  return Number.isFinite(n) ? Math.min(1, Math.max(0, n)) : 0.7
}

// Create the AudioContext (once). Creation alone doesn't need a gesture, but we
// only ever call this — and resume() — from the gesture handler below, never
// from sfx(), so Chrome's autoplay policy is never tripped.
function ensureCtx() {
  if (typeof window === 'undefined') return null
  if (!ctx) {
    const AC = window.AudioContext || window.webkitAudioContext
    if (!AC) return null
    try { ctx = new AC() } catch { return null }
  }
  return ctx
}

function arm() {
  if (armed || typeof window === 'undefined') return
  armed = true
  // Resume INSIDE the user gesture — the only place a resume is allowed without
  // the "AudioContext was not allowed to start" warning.
  const h = () => {
    const c = ensureCtx()
    if (c && c.state === 'suspended') c.resume().catch(() => {})
  }
  window.addEventListener('pointerdown', h, { once: true, passive: true })
  window.addEventListener('keydown', h, { once: true })
}
if (typeof window !== 'undefined') arm()

/** Play a named sound if enabled. Safe before a gesture / SSR (silent no-op). */
export function sfx(name) {
  if (!prefEnabled()) return
  if (name === 'tap' && !prefTap()) return
  const steps = SOUNDS[name]
  if (!steps) return
  // Play ONLY once a user gesture has created + resumed the context (arm()).
  // Never create/resume here — a programmatic sfx() (router tap, post-await
  // login chime) outside a gesture would otherwise trip Chrome's autoplay
  // warning. Before the first gesture we simply stay silent (audio can't play
  // then anyway).
  if (!ctx || ctx.state !== 'running') return
  const ac = ctx
  const vol = prefVolume()
  try {
    for (const s of steps) {
      const osc = ac.createOscillator()
      const g = ac.createGain()
      osc.type = s.type || 'sine'
      osc.frequency.value = s.freq
      const t0 = ac.currentTime + (s.at || 0)
      const dur = s.dur || 0.2
      const peak = Math.max(0.0001, (s.gain || 0.22) * vol)
      g.gain.setValueAtTime(0.0001, t0)
      g.gain.exponentialRampToValueAtTime(peak, t0 + (s.attack || 0.02))
      g.gain.exponentialRampToValueAtTime(0.0001, t0 + dur)
      osc.connect(g).connect(ac.destination)
      osc.start(t0)
      osc.stop(t0 + dur + 0.03)
    }
  } catch {
    /* no audio before a gesture / in a soundless env */
  }
}

export function getSfxEnabled() { return prefEnabled() }
export function setSfxEnabled(on) { try { localStorage.setItem('sfx.enabled', on ? '1' : '0') } catch {} }
export function getSfxTap() { return prefTap() }
export function setSfxTap(on) { try { localStorage.setItem('sfx.tap', on ? '1' : '0') } catch {} }
export function getSfxVolume() { return prefVolume() }
export function setSfxVolume(v) { try { localStorage.setItem('sfx.volume', String(Math.min(1, Math.max(0, v)))) } catch {} }

/** Mirror the server's synced sfx prefs into local storage (called on boot). */
export function applyServerSfxPrefs(data = {}) {
  if (data.sfx_enabled != null) setSfxEnabled(data.sfx_enabled !== '0')
  if (data.sfx_tap != null) setSfxTap(data.sfx_tap === '1')
  if (data.sfx_volume != null) {
    const n = Number(data.sfx_volume)
    if (Number.isFinite(n)) setSfxVolume(n)
  }
}
